/**
 * Walk the DRAFT definition with a set of answers and report what a submit would grant.
 *
 * Runs on `QuestionnaireDefinition` (the canvas state), not on the wire document: the wire
 * conversion still drops 16 eligibility rules and every autoAnswer rule (wire.ts), so a
 * simulation over it would quietly disagree with the engine. Running on the draft also
 * means an unsaved edit can be tried immediately.
 *
 * Semantics are the backend's `QuestionnaireProcessor.process()`:
 *   1. walk from the entry: `nextNodeId` if set, else the FIRST `nextQuestionRules` entry
 *      that is true (first-match-wins)
 *   2. on each question of that path, in path order, evaluate its `programRules` in order;
 *      a hit adds its programs to the list (deduplicated, first grant wins) and writes each
 *      name back into the context as `1`, which is what later `hasKey(...)` reads
 * plus the live FRONTEND's autoAnswer (autoAnswerEngine.ts): a matching rule fills the
 * answer, which the new BD frontend currently loses (TODO P1-1).
 *
 * Answers off the walked path are ignored — they are the `orphanedAnswers` of bd/flow.ts:
 * still in the map after an earlier answer changed the branch, and able to grant a program
 * on the strength of a question nobody is being asked any more.
 */
import type { QuestionnaireDefinition, QuestionnaireNode } from '../types';
import { type EvalResult, evaluateExpression, type Metric } from './astEval';
import { entryNodeOf } from './projection';

export type SimAnswer = string | number | string[];
export type SimAnswers = Record<string, SimAnswer | undefined>;

export interface RuleRun {
  nodeId: string;
  index: number;
  result: EvalResult;
}

export interface Simulation {
  /** nodeIds walked, in order, including the current (unanswered) one. */
  path: string[];
  /** First unanswered question on the path, or null when the walk ended. */
  current: string | null;
  /**
   * `done` = reached a terminal; `stuck` = a non-terminal with no matching exit;
   * `cycle` = the walk came back to a question it had already asked — the draft can never
   * terminate, which must not read as a finished questionnaire.
   */
  end: 'done' | 'stuck' | 'cycle' | null;
  /** Questions answered by an autoAnswer rule, and the value it filled. */
  autoAnswered: Record<string, SimAnswer>;
  /** qualifiedIds answered but not on the path. */
  orphaned: string[];
  /** Every program rule on the path, with its outcome. */
  rules: RuleRun[];
  /** `programList` order: first grant wins. */
  granted: { program: string; nodeId: string; index: number }[];
}

export function isAnswered(v: SimAnswer | undefined): boolean {
  if (v === undefined || v === null || v === '') return false;
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === 'number') return !Number.isNaN(v);
  return true;
}

/** The answer as the engine's context holds it: NUMBER questions as numbers. */
function asContextValue(n: QuestionnaireNode, v: SimAnswer): SimAnswer {
  if (Array.isArray(v)) return v;
  if (n.dataType === 'NUMBER' || n.questionType === 'INPUT_NUMBER') {
    const x = typeof v === 'number' ? v : Number(v);
    return Number.isNaN(x) ? v : x;
  }
  return v;
}

export function simulate(def: QuestionnaireDefinition, answers: SimAnswers): Simulation {
  const byId = new Map(def.nodes.map((n) => [n.nodeId, n]));
  const byQ = new Map(def.nodes.filter((n) => n.qualifiedNodeId).map((n) => [n.qualifiedNodeId!, n]));
  const outIds = (n: QuestionnaireNode) =>
    n.nextNodeId ? [n.nextNodeId] : (n.nextQuestionRules ?? []).map((r) => r.nextQuestionId);

  const path: string[] = [];
  const onPath: Metric = {};
  const autoAnswered: Record<string, SimAnswer> = {};
  let current: string | null = null;
  let end: Simulation['end'] = null;
  const seen = new Set<string>();

  let cur: string | undefined = entryNodeOf(def.nodes)?.nodeId;
  /** An exit pointed at a question that does not exist — a dangling edge, not a cycle. */
  let dangling = false;
  while (cur && !seen.has(cur)) {
    const n = byId.get(cur);
    if (!n) {
      dangling = true;
      break;
    }
    seen.add(cur);
    path.push(cur);
    const q = n.qualifiedNodeId ?? n.nodeId;

    let v = answers[q];
    for (const r of n.autoAnswerRules ?? []) {
      if (evaluateExpression(r.expression, onPath).status === 'hit') {
        const filled = n.multiSelect ? (r.multipleAnswers ?? []) : (r.answer ?? '');
        // A rule that fills nothing answers nothing: leave the question to the user.
        if (!isAnswered(filled)) continue;
        v = filled;
        autoAnswered[cur] = v;
        break;
      }
    }
    if (!isAnswered(v)) {
      current = cur;
      break;
    }
    onPath[q] = asContextValue(n, v!);

    // A terminal ends the walk whatever exits it has — QuestionnaireGenerator.getNextNode
    // returns null for `isLastNode` before looking at nextNodeId or nextQuestionRules.
    if (n.isLastNode) {
      end = 'done';
      cur = undefined;
      break;
    }
    if (n.nextNodeId) {
      cur = n.nextNodeId;
      continue;
    }
    const next = (n.nextQuestionRules ?? []).find(
      (r) => evaluateExpression(r.expression, onPath).status === 'hit',
    );
    if (!next) {
      end = n.isLastNode ? 'done' : 'stuck';
      cur = undefined;
    } else {
      cur = next.nextQuestionId;
    }
  }
  // A dangling exit, or no entry question at all, cannot be walked to an end.
  if (!current && !end && (dangling || path.length === 0)) end = 'stuck';
  // Left the loop because the next question was already asked: a cycle, never a completion.
  if (!current && !end && cur && seen.has(cur)) end = 'cycle';
  if (!current && !end) end = 'done';

  // What can still be asked: anything reachable from the current question.
  const askable = new Set<string>();
  if (current) {
    const stack = [current];
    while (stack.length) {
      const id = stack.pop()!;
      if (askable.has(id)) continue;
      askable.add(id);
      const n = byId.get(id);
      if (n) stack.push(...outIds(n));
    }
  }
  const stillAskable = (qid: string) => {
    const n = byQ.get(qid);
    return !!n && askable.has(n.nodeId);
  };

  const rules: RuleRun[] = [];
  const granted: Simulation['granted'] = [];
  const context: Metric = { ...onPath };
  for (const id of path) {
    const n = byId.get(id)!;
    (n.programRules ?? []).forEach((r, index) => {
      const result = evaluateExpression(r.expression, context, {
        stillAskable,
        isAnswered: (qid) => qid in onPath,
      });
      rules.push({ nodeId: id, index, result });
      if (result.status !== 'hit') return;
      for (const p of r.values) {
        if (granted.some((g) => g.program === p)) continue;
        granted.push({ program: p, nodeId: id, index });
        context[p] = 1;
      }
    });
  }

  const pathQ = new Set(path.map((id) => byId.get(id)?.qualifiedNodeId ?? id));
  const orphaned = Object.keys(answers).filter((q) => isAnswered(answers[q]) && !pathQ.has(q));
  return { path, current, end, autoAnswered, orphaned, rules, granted };
}

/**
 * Ready-made answer sets for the simulation panel, against the merged `q_all` snapshot —
 * the same five as the prototype, so the two can be compared side by side.
 */
export const SIM_SCENARIOS: { id: string; name: string; answers: SimAnswers }[] = [
  {
    id: 'g',
    name: 'Global 新负载 · 大单 + 迁移 IDC',
    answers: {
      Q_0_1: '0015g00000ABCdeAAG', Q_0_2: 'a', Q_1_1_1: 'a', Q_1_1_2_a1: 1200, Q_1_1_2_a2: 400,
      Q_1_1_3: ['e'], Q_1_1_3_1: ['a', 'f'], Q_1_1_4: 'a', Q_1_1_4_1: 'e', Q_1_1_6: 'b',
      Q_1_1_7: 'a', Q_1_1_8: 'c', Q_1_1_9: '5', Q_1_1_10: 15,
    },
  },
  {
    id: 'c',
    name: '中国区 新负载 · 初创',
    answers: {
      Q_0_1: '0015g00000XYZabAAC', Q_0_2: 'a', Q_1_1_1: 'b', Q_1_1_2_b1: 80, Q_1_1_2_b2: 20,
      Q_1_1_3: ['e'], Q_1_1_3_1: ['e'], Q_1_1_4: 'b', Q_1_1_5: 'b', Q_1_1_6: 'b', Q_1_1_7: 'b',
      Q_1_1_8: 'b', Q_1_1_9: '1', Q_1_1_10: 10,
    },
  },
  {
    id: 'r',
    name: '中国区 存量留存',
    answers: {
      Q_0_1: '0015g00000RETenAAA', Q_0_2: 'b', Q_1_2_1: 'b', Q_1_2_2_b1: 200, Q_1_2_2_b2: 900,
      Q_1_2_3: ['b'], Q_1_2_3_1: ['b'], Q_1_2_4: 'b', Q_1_2_5: '3', Q_1_2_6: 8,
    },
  },
  { id: 'p', name: 'PoC · GenAI', answers: { Q_0_1: '0015g00000POCgnAAB', Q_0_2: 'c', Q_1_3_1: 'b' } },
  { id: 'e', name: '其它目的（e）', answers: { Q_0_1: '0015g00000OTHerAAD', Q_0_2: 'e' } },
];
